import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ref as dbRef, get, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { database, storage } from '../../firebase';
import { decryptData } from '../../utils/crypto';
import VideoRecorder from './VideoRecorder';
import LiveAiInterviewViewer from './LiveAiInterviewViewer';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import './AssessmentViewer.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const QUESTION_TYPE_LABEL_KEYS = {
  'short-text': 'assessmentEditor.types.shortText',
  paragraph: 'assessmentEditor.types.paragraph',
  'multiple-choice': 'assessmentEditor.types.multipleChoice',
  checkbox: 'assessmentEditor.types.checkbox',
  'file-upload': 'assessmentEditor.types.fileUpload',
  'record-video': 'assessmentEditor.types.recordVideo'
};

const QUESTION_TYPE_ICONS = {
  'short-text': 'fa-solid fa-font',
  paragraph: 'fa-solid fa-align-left',
  'multiple-choice': 'fa-solid fa-circle-dot',
  checkbox: 'fa-solid fa-square-check',
  'file-upload': 'fa-solid fa-file-arrow-up',
  'record-video': 'fa-solid fa-video'
};

const answerHasValue = (answer) => (
  Array.isArray(answer)
    ? answer.length > 0
    : !!answer && answer.toString().trim() !== ''
);

const getFirstOpenQuestionIndex = (questions = [], answerMap = {}) => {
  if (!questions.length) return 0;
  const firstOpenIndex = questions.findIndex((question) => !answerHasValue(answerMap[question.id]));
  return firstOpenIndex >= 0 ? firstOpenIndex : questions.length - 1;
};

// Helper to support both AES-encrypted and simple Base64 slugs
const decodeSlug = (raw) => {
  if (!raw) return null;

  console.log('DEBUG: Attempting to decode slug:', raw?.substring(0, 50) + '...');

  // 1. Try AES decryption first (for encrypted slugs from Applicants.js)
  try {
    const aes = decryptData(raw);
    if (aes && aes.includes('_')) {
      console.log('DEBUG: AES decryption successful');
      return aes;
    }
  } catch (e) {
    console.log('DEBUG: AES decryption failed, trying Base64 variants');
  }

  // 2. Try URL-decoded then Base64 (for btoa-encoded slugs that may be URL-encoded)
  try {
    const urlDecoded = decodeURIComponent(raw);
    const decoded = atob(urlDecoded);
    if (decoded && decoded.includes('_')) {
      console.log('DEBUG: URL-decoded Base64 successful');
      return decoded;
    }
  } catch (e) {
    // Continue to next method
  }

  // 3. Try standard Base64 directly (for btoa-encoded slugs from ApplicantsWorkspace.js)
  try {
    const decoded = atob(raw);
    if (decoded && decoded.includes('_')) {
      console.log('DEBUG: Standard Base64 successful');
      return decoded;
    }
  } catch (e) {
    // Continue to next method
  }

  // 4. Try URL-safe Base64 variants (with padding restoration)
  try {
    let padded = raw.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed
    while (padded.length % 4 !== 0) {
      padded += '=';
    }
    const decoded = atob(padded);
    if (decoded && decoded.includes('_')) {
      console.log('DEBUG: URL-safe Base64 successful');
      return decoded;
    }
  } catch (e) {
    // Continue to next method
  }

  console.error('DEBUG: All decoding methods failed for slug');
  return null;
};


const LegacyAssessmentViewer = () => {
  useUiLanguage();
  const { slug } = useParams();
  const { currentUser } = useAuth();
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [decodedInfo, setDecodedInfo] = useState(null);
  const [uploads, setUploads] = useState({});
  const [uploading, setUploading] = useState({});
  const [applicantEmail, setApplicantEmail] = useState('');
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [lastActiveTime, setLastActiveTime] = useState(null);
  const [isSubmissionInProgress, setIsSubmissionInProgress] = useState(false);
  const [submissionLocked, setSubmissionLocked] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    const attemptDecode = () => {
      const decoded = decodeSlug(slug);
      if (decoded) {
        const parts = decoded.split('_');
        if (parts.length >= 3) {
          // Handle imported candidates with multiple underscores in their IDs
          // Format: companyId_assessmentId_applicantId (where applicantId may contain underscores)
          const companyId = parts[0];
          const assessmentId = parts[1];
          const applicantId = parts.slice(2).join('_'); // Join remaining parts back together

          console.log('DEBUG: Decoded assessment info:', { companyId, assessmentId, applicantId });
          setDecodedInfo({ companyId, assessmentId, applicantId });

          // Determine if this is an imported or authenticated candidate and fetch email accordingly
          const fetchApplicantEmail = async () => {
            try {
              console.log('DEBUG: Fetching email for applicant:', applicantId);

              // First, try to get email from imported profiles (for imported candidates)
              if (applicantId.startsWith('temp_')) {
                console.log('DEBUG: Detected imported candidate, checking imported_profiles');
                // Try NEW path first
                let importedProfileRef = dbRef(database, `imported_profiles/${companyId}/${applicantId}`);
                let importedSnapshot = await get(importedProfileRef);

                // Fallback to legacy path
                if (!importedSnapshot.exists()) {
                  console.log('DEBUG: Not found at new path, trying legacy path');
                  importedProfileRef = dbRef(database, `imported_profiles/${applicantId}`);
                  importedSnapshot = await get(importedProfileRef);
                }

                if (importedSnapshot.exists()) {
                  const profileData = importedSnapshot.val();
                  console.log('DEBUG: Found imported profile, email:', profileData.email);
                  setApplicantEmail(profileData.email || '');
                  return;
                } else {
                  console.log('DEBUG: No imported profile found');
                }
              }

              // If not found in imported profiles, try authenticated user email via cloud function
              console.log('DEBUG: Fetching authenticated user email via cloud function');
              const response = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ uid: applicantId })
              });

              const data = await response.json();
              console.log('DEBUG: Cloud function response:', data);
              setApplicantEmail(data.email || '');
            } catch (err) {
              console.error('Error fetching applicant email:', err);
              // Don't fail the assessment loading if email fetch fails
              setApplicantEmail('');
            }
          };

          fetchApplicantEmail();
          return true;
        }
      }
      return false;
    };

    const decodedOk = attemptDecode();

    // If decoding failed but we have a logged-in user, treat slug as raw assessmentId for owner preview
    if (!decodedOk) {
      if (currentUser) {
        setDecodedInfo({ companyId: currentUser.uid, assessmentId: slug, applicantId: currentUser.uid });
      } else {
        console.error('Error decoding slug and no authenticated user.');
        setAuthError('This assessment link is invalid or has expired. Please contact your recruiter for a new link.');
        setLoading(false);
      }
    }
  }, [slug, currentUser]);

  useEffect(() => {
    if (!decodedInfo) return;

    const { companyId, assessmentId, applicantId } = decodedInfo;

    const loadAssessment = async () => {
      console.log('DEBUG: Loading assessment for:', { companyId, assessmentId, applicantId });

      // Determine connection key pattern for imported vs authenticated candidates
      let connectionKey;
      if (applicantId.startsWith('temp_')) {
        connectionKey = `${companyId}_imported_${applicantId}`;
        console.log('DEBUG: Using imported candidate connection pattern:', connectionKey);
      } else {
        connectionKey = `${companyId}_${applicantId}`;
        console.log('DEBUG: Using authenticated candidate connection pattern:', connectionKey);
      }

      try {
        let assessmentData;
        let savedProgressData = null;

        // Check if this is an employer previewing their own assessment
        const isEmployerPreview = currentUser && companyId === currentUser.uid;

        if (isEmployerPreview) {
          // EMPLOYER PREVIEW MODE: Use direct Firebase read (authenticated)
          console.log('DEBUG: Employer preview mode - using direct Firebase read');

          // Check for existing submission (for employer preview, use Firebase)
          const submissionRef = dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/response`);
          const submissionSnapshot = await get(submissionRef);
          if (submissionSnapshot.exists()) {
            setAlreadySubmitted(true);
            setSubmissionLocked(true);
            setSubmitted(true);
            setLoading(false);
            return;
          }

          const snapshot = await get(dbRef(database, `companies/${companyId}/assessments/${assessmentId}`));
          if (!snapshot.exists()) {
            setAuthError('Assessment not found.');
            setLoading(false);
            return;
          }
          assessmentData = snapshot.val();

          // Handle job-varying for employer preview (show first job's questions or base questions)
          if (assessmentData.varyByJob && assessmentData.jobQuestions) {
            const firstJobId = Object.keys(assessmentData.jobQuestions)[0];
            if (firstJobId) {
              const jobData = assessmentData.jobQuestions[firstJobId];
              assessmentData = {
                ...assessmentData,
                questions: jobData.questions || [],
                duration_minutes: jobData.duration_minutes || assessmentData.duration_minutes
              };
            }
          }

          // Load progress for employer preview
          const progressRef = dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/progress`);
          const progressSnapshot = await get(progressRef);
          if (progressSnapshot.exists()) {
            savedProgressData = progressSnapshot.val();
          }
        } else {
          // CANDIDATE MODE: Use backend API to bypass Firebase security rules completely
          console.log('DEBUG: Candidate mode - using backend API (no direct Firebase reads)');
          const response = await fetch(`${BACKEND_URL}/get-assessment-for-candidate/${slug}`);
          const result = await response.json();

          if (!result.success) {
            console.error('Failed to load assessment via backend:', result.error);
            setAuthError(result.error || 'Failed to load assessment. Please try again or contact your recruiter.');
            setLoading(false);
            return;
          }

          // Handle already submitted case (from backend check)
          if (result.already_submitted) {
            console.log('DEBUG: Assessment already submitted (from backend)');
            setAlreadySubmitted(true);
            setSubmissionLocked(true);
            setSubmitted(true);
            setLoading(false);
            return;
          }

          if (!result.assessment) {
            setAuthError('Assessment not found.');
            setLoading(false);
            return;
          }

          assessmentData = result.assessment;
          savedProgressData = result.saved_progress; // From backend

          console.log('DEBUG: Assessment loaded via backend API:', {
            title: assessmentData.title,
            questionCount: assessmentData.questions?.length,
            hasProgress: !!savedProgressData
          });
        }

        // SAFEGUARD: Ensure questions is always an array
        if (!assessmentData.questions) {
          console.warn('DEBUG: assessmentData.questions is undefined, defaulting to empty array');
          assessmentData.questions = [];
        }

        console.log('DEBUG: Assessment data loaded successfully:', { title: assessmentData.title, questionCount: assessmentData.questions.length });
        setAssessment(assessmentData);

        if (assessmentData.duration_minutes) {
          // Handle saved progress (from either Firebase for employer or backend API for candidate)
          if (savedProgressData) {
            console.log('DEBUG: Found saved progress:', {
              timeRemaining: savedProgressData.timeRemaining,
              answersCount: Object.keys(savedProgressData.answers || {}).length,
              lastSaved: savedProgressData.lastSaved
            });

            const validTimeRemaining = savedProgressData.timeRemaining && savedProgressData.timeRemaining > 0
              ? savedProgressData.timeRemaining
              : assessmentData.duration_minutes * 60;

            console.log('🔎 Setting time remaining to:', validTimeRemaining, 'seconds');

            setTimeRemaining(validTimeRemaining);
            setAnswers(savedProgressData.answers || {});
            const savedQuestionIndex = Number(savedProgressData.currentQuestionIndex);
            const restoredQuestionIndex = Number.isFinite(savedQuestionIndex)
              ? Math.min(Math.max(savedQuestionIndex, 0), Math.max(assessmentData.questions.length - 1, 0))
              : getFirstOpenQuestionIndex(assessmentData.questions, savedProgressData.answers || {});
            setCurrentQuestionIndex(restoredQuestionIndex);

            // Restore checkbox and multiple choice answers
            Object.keys(savedProgressData.answers || {}).forEach(qId => {
              const answer = savedProgressData.answers[qId];
              if (Array.isArray(answer)) {
                setTimeout(() => {
                  answer.forEach(value => {
                    const checkbox = document.querySelector(`input[type="checkbox"][value="${value}"]`);
                    if (checkbox) checkbox.checked = true;
                  });
                }, 100);
              } else if (typeof answer === 'string') {
                setTimeout(() => {
                  const radio = document.querySelector(`input[type="radio"][value="${answer}"]`);
                  if (radio) radio.checked = true;
                }, 100);
              }
            });
            setSessionStartTime(savedProgressData.sessionStartTime || new Date().toISOString());
            setLastActiveTime(savedProgressData.lastActiveTime);

            const timeSinceLastSave = savedProgressData.lastSaved ?
              Math.round((new Date() - new Date(savedProgressData.lastSaved)) / 1000 / 60) : 0;

            if (timeSinceLastSave > 0) {
              console.log(`🔄 Continuing assessment from ${timeSinceLastSave} minutes ago`);
              setTimeout(() => {
                showRecoveryNotification(timeSinceLastSave, savedProgressData.answeredQuestions || 0);
              }, 1000);
            }
          } else {
            console.log('DEBUG: No saved progress found, starting fresh with', assessmentData.duration_minutes, 'minutes');
            const startTime = new Date().toISOString();
            setTimeRemaining(assessmentData.duration_minutes * 60);
            setSessionStartTime(startTime);
            setLastActiveTime(startTime);
          }

          // Mark progress as loaded AFTER all state is set
          setTimeout(() => {
            setProgressLoaded(true);
            console.log('✅ Progress loading completed - timer can now start');
          }, 100);
        } else {
          setProgressLoaded(true);
        }
      } catch (error) {
        console.error('Error loading assessment:', error);
        setAuthError('An error occurred while loading the assessment.');
      } finally {
        setLoading(false);
      }
    };
    loadAssessment();
  }, [decodedInfo]);

  // Enhanced progress saving with better state management
  useEffect(() => {
    if (!assessment || !assessmentStarted || submitted || timeRemaining === null || !decodedInfo) return;

    const saveProgress = async () => {
      try {
        const { companyId, assessmentId, applicantId } = decodedInfo;

        // Use correct connection key pattern
        let connectionKey;
        if (applicantId.startsWith('temp_')) {
          connectionKey = `${companyId}_imported_${applicantId}`;
        } else {
          connectionKey = `${companyId}_${applicantId}`;
        }

        const now = new Date().toISOString();
        setLastActiveTime(now);

        const progressData = {
          timeRemaining,
          answers,
          lastSaved: now,
          sessionStartTime: sessionStartTime || now,
          lastActiveTime: now,
          currentQuestionIndex: getCurrentQuestionIndex(),
          answeredQuestions: getAnsweredCount(),
          totalQuestions: assessment.questions.length,
          status: 'in_progress'
        };

        // Check if this is an employer previewing their own assessment
        const isEmployerPreview = currentUser && companyId === currentUser.uid;

        if (isEmployerPreview) {
          // EMPLOYER PREVIEW: Use direct Firebase write
          const progressRef = dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/progress`);
          await set(progressRef, progressData);
        } else {
          // CANDIDATE: Use backend API to bypass Firebase security rules
          await fetch(`${BACKEND_URL}/save-assessment-progress`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              slug: slug,
              progress_data: progressData
            })
          });
        }

        console.log('💾 Progress saved:', {
          timeRemaining,
          answeredCount: getAnsweredCount(),
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error saving progress:', error);
      }
    };

    // COST-OPTIMIZED: Save progress only when user makes changes (debounced)
    // No more wasteful automatic saving every 5 seconds!
    const debouncedSave = debounce(saveProgress, 2000); // Save 2 seconds after last change
    debouncedSave();

    return () => {
      debouncedSave.cancel?.(); // Cancel pending saves on cleanup
    };
  }, [answers, decodedInfo, assessment, submitted, assessmentStarted, currentQuestionIndex]); // Save answer and position changes, not timer ticks

  // COST-OPTIMIZED: Save only on critical events, not continuously
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && assessmentStarted && !submitted && decodedInfo) {
        // User switched tabs or minimized - save immediately (CRITICAL event)
        saveProgressImmediate();
        console.log('💾 CRITICAL: Saved progress on tab switch/minimize');
      }
    };

    const handleBeforeUnload = (e) => {
      if (assessmentStarted && !submitted && !submissionLocked && decodedInfo && (getAnsweredCount() > 0 || timeRemaining < (assessment?.duration_minutes * 60 || 0))) {
        // Save progress before page unload - CRITICAL for data preservation
        saveProgressImmediate();
        markSessionInterrupted();
        console.log('💾 CRITICAL: Saved progress before page unload');

        // Show warning for accidental closes
        e.preventDefault();
        e.returnValue = 'You have an assessment in progress. Your answers will be saved, but leaving now may affect your remaining time. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    const handlePageHide = () => {
      // Page is being hidden (mobile app switch, tab close, etc.)
      if (assessmentStarted && !submitted && !submissionLocked && decodedInfo) {
        saveProgressImmediate();
        markSessionInterrupted();
        console.log('💾 CRITICAL: Saved progress on page hide (mobile)');
      }
    };

    const handleFocus = () => {
      // User returned to the page
      if (assessmentStarted && !submitted && !submissionLocked && decodedInfo) {
        console.log('🔄 Page regained focus - checking for session recovery');
        checkForSessionRecovery();
      }
    };

    // COST-OPTIMIZED: Also save when user completes a question (not on every keystroke)
    const handleQuestionComplete = () => {
      if (assessmentStarted && !submitted && !submissionLocked && decodedInfo) {
        // Use debounced save to avoid excessive writes
        const debouncedQuestionSave = debounce(saveProgressImmediate, 3000);
        debouncedQuestionSave();
        console.log('💾 SMART: Debounced save triggered by question completion');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide); // For mobile Safari
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('focus', handleFocus);
    };
  }, [submitted, decodedInfo, answers, assessmentStarted, currentQuestionIndex]);

  const saveProgressImmediate = async () => {
    if (!assessment || submitted || timeRemaining === null || !decodedInfo) return;

    try {
      const { companyId, assessmentId, applicantId } = decodedInfo;

      let connectionKey;
      if (applicantId.startsWith('temp_')) {
        connectionKey = `${companyId}_imported_${applicantId}`;
      } else {
        connectionKey = `${companyId}_${applicantId}`;
      }

      const now = new Date().toISOString();

      const progressRef = dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/progress`);
      await set(progressRef, {
        timeRemaining,
        answers,
        lastSaved: now,
        sessionStartTime: sessionStartTime || now,
        lastActiveTime: now,
        currentQuestionIndex: getCurrentQuestionIndex(),
        answeredQuestions: getAnsweredCount(),
        totalQuestions: assessment.questions.length,
        status: 'in_progress'
      });

      console.log('✅ Immediate progress save completed');
    } catch (error) {
      console.error('Error in immediate progress save:', error);
    }
  };

  const getCurrentQuestionIndex = () => {
    if (!assessment?.questions?.length) return 0;
    return Math.min(Math.max(currentQuestionIndex, 0), assessment.questions.length - 1);
  };

  const showRecoveryNotification = (minutesAgo, answeredQuestions) => {
    // Create a temporary notification overlay
    const notification = document.createElement('div');
    notification.className = 'av-recovery-notice';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #28a745, #20c997);
      color: white;
      padding: 20px;
      border-radius: 12px;
      box-shadow: 0 8px 25px rgba(40, 167, 69, 0.3);
      z-index: 10000;
      max-width: 350px;
      font-family: 'Montserrat', sans-serif;
      animation: slideInRight 0.5s ease-out;
    `;

    notification.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <i class="fa-solid fa-clock-rotate-left" style="margin-right: 10px; font-size: 18px;"></i>
        <strong style="font-size: 16px;">Assessment Restored</strong>
      </div>
      <div style="font-size: 14px; line-height: 1.4; margin-bottom: 12px;">
        Welcome back! Your progress from ${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago has been restored.
        <br/><span style="opacity: 0.9;">Answered: ${answeredQuestions} questions</span>
      </div>
      <div style="font-size: 12px; opacity: 0.8;">This message will disappear in 10 seconds</div>
    `;

    // Add CSS animation keyframes to document if not exists
    if (!document.querySelector('#slideInRightKeyframes')) {
      const style = document.createElement('style');
      style.id = 'slideInRightKeyframes';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
        @keyframes slideInDown {
          from { transform: translate(-50%, -100%); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }
        @keyframes slideOutUp {
          from { transform: translate(-50%, 0); opacity: 1; }
          to { transform: translate(-50%, -100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(notification);

    // Remove notification after 10 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOutRight 0.5s ease-in';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 500);
    }, 10000);
  };

  const markSessionInterrupted = async () => {
    if (!decodedInfo) return;

    try {
      const { companyId, assessmentId, applicantId } = decodedInfo;

      let connectionKey;
      if (applicantId.startsWith('temp_')) {
        connectionKey = `${companyId}_imported_${applicantId}`;
      } else {
        connectionKey = `${companyId}_${applicantId}`;
      }

      const interruptionRef = dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/session_interruption`);
      await set(interruptionRef, {
        interruptedAt: new Date().toISOString(),
        timeRemainingAtInterruption: timeRemaining,
        answeredQuestions: getAnsweredCount(),
        sessionId: sessionStartTime,
        reason: 'abrupt_exit'
      });

      console.log('🚨 Session interruption marked for recovery');
    } catch (error) {
      console.error('Failed to mark session interruption:', error);
    }
  };

  const checkForSessionRecovery = async () => {
    if (!decodedInfo) return;

    try {
      const { companyId, assessmentId, applicantId } = decodedInfo;

      let connectionKey;
      if (applicantId.startsWith('temp_')) {
        connectionKey = `${companyId}_imported_${applicantId}`;
      } else {
        connectionKey = `${companyId}_${applicantId}`;
      }

      const interruptionRef = dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/session_interruption`);
      const interruptionSnapshot = await get(interruptionRef);

      if (interruptionSnapshot.exists()) {
        const interruption = interruptionSnapshot.val();
        const timeSinceInterruption = Math.round((new Date() - new Date(interruption.interruptedAt)) / 1000 / 60);

        console.log('🔄 Recovering from session interruption:', {
          timeSinceInterruption,
          timeRemaining: interruption.timeRemainingAtInterruption
        });

        // Clear interruption marker
        await set(interruptionRef, null);

        // Show recovery notification
        showSessionRecoveryNotification(timeSinceInterruption);
      }
    } catch (error) {
      console.error('Failed to check session recovery:', error);
    }
  };

  const showSessionRecoveryNotification = (minutesAgo) => {
    const notification = document.createElement('div');
    notification.className = 'av-recovery-notice';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, #17a2b8, #138496);
      color: white;
      padding: 20px;
      border-radius: 12px;
      box-shadow: 0 8px 25px rgba(23, 162, 184, 0.3);
      z-index: 10000;
      max-width: 400px;
      font-family: 'Montserrat', sans-serif;
      text-align: center;
      animation: slideInDown 0.5s ease-out;
    `;

    notification.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 8px;">
        <i class="fa-solid fa-rotate-right" style="margin-right: 10px; font-size: 18px;"></i>
        <strong style="font-size: 16px;">Session Recovered</strong>
      </div>
      <div style="font-size: 14px; line-height: 1.4; margin-bottom: 12px;">
        Your assessment session was interrupted ${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago.<br/>
        <span style="opacity: 0.9;">All your progress has been restored.</span>
      </div>
      <div style="font-size: 12px; opacity: 0.8;">You can continue where you left off</div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = 'slideOutUp 0.5s ease-in';
        setTimeout(() => {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 500);
      }
    }, 8000);
  };

  // Timer countdown - WORKING VERSION
  useEffect(() => {
    if (!assessmentStarted) {
      console.log('⏸️ Timer waiting for candidate to begin assessment...');
      return;
    }

    // Don't start timer until progress is loaded
    if (!progressLoaded) {
      console.log('⏸️ Timer waiting for progress to load...');
      return;
    }

    // Don't run timer if assessment is finished or invalid
    if (timeRemaining === null || timeRemaining <= 0 || submitted || submissionLocked) {
      console.log('⏹️ Timer stopped - assessment finished or invalid state');
      return;
    }

    // Start the timer if not already started
    if (!timerStarted) {
      console.log('🕐 STARTING timer with', timeRemaining, 'seconds remaining');
      setTimerStarted(true);
    }

    console.log('⏱️ Timer effect running - current time:', timeRemaining, 'seconds');

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1;
        console.log('⏰ Tick - time remaining:', newTime);

        if (newTime <= 0) {
          console.log('🚨 Time expired - auto-submitting assessment');
          submitAssessment();
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => {
      console.log('🧹 Cleaning up timer');
      clearInterval(timer);
    };
  }, [assessmentStarted, progressLoaded, submitted, submissionLocked]); // Only depend on state flags, NOT timeRemaining

  useEffect(() => {
    if (!assessmentStarted) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [assessmentStarted, currentQuestionIndex]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Debounce utility to prevent excessive Firebase writes
  const debounce = (func, wait) => {
    let timeout;
    const debounced = (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
    debounced.cancel = () => clearTimeout(timeout);
    return debounced;
  };

  const handleAnswerChange = (qId, value) => {
    setAnswers((prev) => ({ ...prev, [qId]: value }));
    // Progress will auto-save via useEffect when answers change (debounced)
  };

  const handleFileUpload = async (questionId, file) => {
    if (!file) return;
    const { companyId, applicantId, assessmentId } = decodedInfo;

    // Use correct connection key pattern for file uploads
    let connectionKey;
    if (applicantId.startsWith('temp_')) {
      connectionKey = `${companyId}_imported_${applicantId}`;
    } else {
      connectionKey = `${companyId}_${applicantId}`;
    }

    // FIXED: Handle undefined file names properly
    const fileName = file.name || `upload_${questionId}_${Date.now()}`;

    setUploading(prev => ({ ...prev, [questionId]: true }));

    try {
      // Check if this is an employer previewing their own assessment
      const isEmployerPreview = currentUser && companyId === currentUser.uid;

      let url;

      if (isEmployerPreview) {
        // EMPLOYER PREVIEW: Use direct Firebase Storage upload
        const filePath = `connections/${connectionKey}/assessments/${assessmentId}/uploads/${questionId}-${fileName}`;
        const fileRef = storageRef(storage, filePath);
        const snapshot = await uploadBytes(fileRef, file);
        url = await getDownloadURL(snapshot.ref);
      } else {
        // CANDIDATE: Use backend API to bypass Firebase security rules
        console.log('DEBUG: Uploading file via backend API');

        // Convert file to base64
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result.split(',')[1]; // Remove data:mime;base64, prefix
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const response = await fetch(`${BACKEND_URL}/upload-assessment-file`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slug: slug,
            question_id: questionId,
            file_name: fileName,
            file_data: base64Data,
            content_type: file.type || 'application/octet-stream'
          })
        });

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Upload failed');
        }

        url = result.download_url;
        console.log('DEBUG: File uploaded via backend:', url);
      }

      handleAnswerChange(questionId, url);
      setUploads(prev => ({ ...prev, [questionId]: { name: fileName, url } }));
    } catch (error) {
      console.error("Upload failed:", error);
      alert(uiText("File upload failed. Please try again."));
    } finally {
      setUploading(prev => ({ ...prev, [questionId]: false }));
    }
  };

  // NEW: Separate handler for video recordings (already uploaded by VideoRecorder)
  const handleVideoRecordingComplete = (questionId, downloadURL) => {
    console.log('Video recording completed:', questionId, downloadURL);
    // Video is already uploaded by VideoRecorder, just save the URL
    handleAnswerChange(questionId, downloadURL);
    setUploads(prev => ({
      ...prev,
      [questionId]: {
        name: `video_${questionId}_${Date.now()}.webm`,
        url: downloadURL
      }
    }));
  };

  const isQuestionAnswered = (question) => {
    if (!question) return false;
    return answerHasValue(answers[question.id]);
  };

  const getQuestionTypeLabel = (type) => {
    return t(QUESTION_TYPE_LABEL_KEYS[type] || QUESTION_TYPE_LABEL_KEYS['short-text']);
  };

  const getQuestionTypeIcon = (type) => {
    return QUESTION_TYPE_ICONS[type] || QUESTION_TYPE_ICONS['short-text'];
  };

  const handleBeginAssessment = () => {
    const now = new Date().toISOString();
    if (!sessionStartTime) {
      setSessionStartTime(now);
    }
    setLastActiveTime(now);
    setAssessmentStarted(true);
  };

  const handlePreviousQuestion = () => {
    setCurrentQuestionIndex(prev => Math.max(prev - 1, 0));
  };

  const handleNextQuestion = () => {
    const questions = assessment?.questions || [];
    const question = questions[getCurrentQuestionIndex()];

    if (!question || !isQuestionAnswered(question) || uploading[question.id]) return;

    setCurrentQuestionIndex(prev => Math.min(prev + 1, questions.length - 1));
  };

  const renderAnswerInput = (q) => {
    if (!q) return null;

    if (q.type === 'short-text') {
      return (
        <input
          type="text"
          className="av-input"
          placeholder={t('assessmentViewer.answerPlaceholder')}
          value={answers[q.id] || ''}
          onChange={(e) => handleAnswerChange(q.id, e.target.value)}
        />
      );
    }

    if (q.type === 'paragraph') {
      return (
        <textarea
          rows="5"
          className="av-textarea"
          placeholder={t('assessmentViewer.answerDetailed')}
          value={answers[q.id] || ''}
          onChange={(e) => handleAnswerChange(q.id, e.target.value)}
        />
      );
    }

    if (q.type === 'multiple-choice' && q.options) {
      return (
        <div className="av-option-list">
          {q.options.map((opt, oIdx) => (
            <label key={oIdx} className={`av-option ${answers[q.id] === opt ? 'selected' : ''}`}>
              <input
                type="radio"
                name={q.id}
                value={opt}
                checked={answers[q.id] === opt}
                onChange={() => handleAnswerChange(q.id, opt)}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    }

    if (q.type === 'checkbox' && q.options) {
      return (
        <div className="av-option-list">
          {q.options.map((opt, oIdx) => (
            <label key={oIdx} className={`av-option ${(answers[q.id] || []).includes(opt) ? 'selected' : ''}`}>
              <input
                type="checkbox"
                value={opt}
                checked={(answers[q.id] || []).includes(opt)}
                onChange={(e) => {
                  const existing = answers[q.id] || [];
                  const newArr = e.target.checked
                    ? [...existing, opt]
                    : existing.filter(v => v !== opt);
                  handleAnswerChange(q.id, newArr);
                }}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    }

    if (q.type === 'file-upload') {
      const uploadedUrl = uploads[q.id]?.url || answers[q.id];
      const uploadedName = uploads[q.id]?.name || t('assessmentViewer.uploadedAnswer');

      return (
        <div className="av-upload-box">
          <i className="fa-solid fa-cloud-upload-alt"></i>
          <label className={`av-file-control ${uploading[q.id] ? 'disabled' : ''}`}>
            <span>{uploadedUrl ? t('assessmentViewer.replaceFile') : t('assessmentViewer.chooseFile')}</span>
            <input
              type="file"
              onChange={(e) => handleFileUpload(q.id, e.target.files[0])}
              disabled={uploading[q.id]}
            />
          </label>
          {uploading[q.id] && (
            <p className="av-upload-status">
              <i className="fa-solid fa-spinner fa-spin"></i>
              {t('assessmentViewer.uploading')}
            </p>
          )}
          {uploadedUrl && !uploading[q.id] && (
            <p className="av-upload-status success">
              <i className="fa-solid fa-check-circle"></i>
              {t('assessmentViewer.uploadedLabel')}{' '}
              <a href={uploadedUrl} target="_blank" rel="noopener noreferrer">{uploadedName}</a>
            </p>
          )}
        </div>
      );
    }

    if (q.type === 'record-video') {
      return (
        <div className="av-video-answer">
          {answers[q.id] && (
            <div className="av-saved-answer">
              <i className="fa-solid fa-check-circle"></i>
              <span>{t('assessmentViewer.videoSaved')}</span>
              <a href={answers[q.id]} target="_blank" rel="noopener noreferrer">
                {t('assessmentViewer.viewRecording')}
              </a>
            </div>
          )}
          <VideoRecorder
            questionId={q.id}
            onRecordingComplete={handleVideoRecordingComplete}
            decodedInfo={decodedInfo}
          />
        </div>
      );
    }

    return null;
  };

  const submitAssessment = async () => {
    // Prevent multiple submissions
    if (submitting || isSubmissionInProgress || submissionLocked || submitted) {
      console.log('Submission blocked - already in progress or completed');
      return;
    }

    // Show confirmation dialog
    const userConfirmed = window.confirm(
      'Are you sure you want to submit your assessment? You cannot make changes after submission.'
    );

    if (!userConfirmed) {
      return;
    }

    setSubmitting(true);
    setIsSubmissionInProgress(true);
    setSubmissionLocked(true);

    try {
      const { companyId, assessmentId, applicantId } = decodedInfo;

      // Use correct connection key pattern
      let connectionKey;
      if (applicantId.startsWith('temp_')) {
        connectionKey = `${companyId}_imported_${applicantId}`;
      } else {
        connectionKey = `${companyId}_${applicantId}`;
      }

      const submissionData = {
        userId: applicantId,
        userEmail: applicantEmail,
        submittedAt: new Date().toISOString(),
        answers,
        // Save the questions that were actually asked (crucial for job-varying assessments)
        questions: assessment.questions || [],
        timeUsed: assessment.duration_minutes ? (assessment.duration_minutes * 60) - timeRemaining : null,
        sessionStartTime: sessionStartTime,
        lastActiveTime: lastActiveTime,
        totalTimeSpent: sessionStartTime ? Math.round((new Date() - new Date(sessionStartTime)) / 1000) : null,
        answeredQuestions: getAnsweredCount(),
        totalQuestions: assessment.questions.length,
        status: 'completed',
        submissionLocked: true
      };

      // Check if this is an employer previewing their own assessment
      const isEmployerPreview = currentUser && companyId === currentUser.uid;

      if (isEmployerPreview) {
        // EMPLOYER PREVIEW: Use direct Firebase writes
        // Check for existing submission first
        const [existingResponseRef, existingLockRef, existingEvaluationRef] = [
          dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/response`),
          dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/submission_lock`),
          dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/evaluation`)
        ];

        const [existingSnapshot, lockSnapshot, evaluationSnapshot] = await Promise.all([
          get(existingResponseRef),
          get(existingLockRef),
          get(existingEvaluationRef)
        ]);

        const alreadySubmittedCheck = existingSnapshot.exists() ||
          (lockSnapshot.exists() && lockSnapshot.val()?.locked) ||
          evaluationSnapshot.exists();

        if (alreadySubmittedCheck) {
          console.log('⚠️ Assessment already submitted - blocking duplicate submission!');
          setAlreadySubmitted(true);
          setSubmitted(true);
          setSubmissionLocked(true);
          return;
        }

        // Submit using direct Firebase
        const responseRef = dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/response`);
        await set(responseRef, submissionData);

        const progressRef = dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/progress`);
        await set(progressRef, null);

        const lockRef = dbRef(database, `connections/${connectionKey}/assessments/${assessmentId}/submission_lock`);
        await set(lockRef, {
          locked: true,
          lockedAt: new Date().toISOString(),
          applicantId: applicantId,
          sessionId: sessionStartTime,
          lockId: `${applicantId}_${Date.now()}`
        });
      } else {
        // CANDIDATE: Use backend API to bypass Firebase security rules
        console.log('DEBUG: Submitting via backend API');
        const response = await fetch(`${BACKEND_URL}/submit-assessment-response`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slug: slug,
            submission_data: submissionData
          })
        });

        const result = await response.json();

        if (!result.success) {
          if (result.already_submitted) {
            console.log('⚠️ Assessment already submitted (from backend)');
            setAlreadySubmitted(true);
            setSubmitted(true);
            setSubmissionLocked(true);
            return;
          }
          throw new Error(result.error || 'Failed to submit assessment');
        }
      }

      console.log('✅ Assessment submitted successfully');

      // Candidate submissions are evaluated by the trusted submit endpoint.
      // Keeping the privileged evaluator out of the browser prevents a caller
      // from asking it to score an arbitrary company/candidate combination.
      // Employer previews deliberately remain non-authoritative and therefore
      // never create a candidate score-history event.

      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      alert(uiText('Failed to submit assessment. Please try again.'));

      // Reset submission states on error to allow retry
      setSubmissionLocked(false);
      setIsSubmissionInProgress(false);
    } finally {
      setSubmitting(false);
    }
  };

  const getAnsweredCount = () => {
    if (!assessment?.questions?.length) return 0;
    return assessment.questions.filter((question) => isQuestionAnswered(question)).length;
  };

  if (loading) {
    return (
      <div data-assessment-state="loading" style={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#031128',
        color: '#FFFFFF',
        fontFamily: 'Montserrat'
      }}>
        <div style={{ textAlign: 'center' }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '48px', marginBottom: '20px' }}></i>
          <p style={{ fontSize: '20px' }}>{uiText("Loading assessment...")}</p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div data-assessment-state="authError" style={{
        minHeight: '100vh',
        backgroundColor: '#031128',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Montserrat'
      }}>
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '500px',
          width: '90%'
        }}>
          <i className="fa-solid fa-exclamation-triangle" style={{ fontSize: '64px', color: '#f44336', marginBottom: '20px' }}></i>
          <h2 style={{ color: "var(--app-neutral-ink, #000)", marginBottom: '15px' }}>{uiText("Invalid Link")}</h2>
          <p style={{ color: '#666', fontSize: '16px', margin: 0 }}>
            {authError || uiText("This assessment link is invalid or has expired.")}<br />{uiText("Please contact your recruiter or hiring manager for a new invitation.")}</p>
        </div>
      </div>
    );
  }

  if (alreadySubmitted) {
    return (
      <div data-assessment-state="alreadySubmitted" style={{
        minHeight: '100vh',
        backgroundColor: '#031128',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '500px',
          width: '90%'
        }}>
          <i className="fa-solid fa-check-circle" style={{ fontSize: '64px', color: '#28a745', marginBottom: '20px' }}></i>
          <h2 style={{ color: "var(--app-neutral-ink, #000)", marginBottom: '15px' }}>{t('assessmentViewer.alreadySubmittedTitle')}</h2>
          <p style={{ color: '#666', fontSize: '16px', margin: 0 }}>
            {t('assessmentViewer.alreadySubmittedBody')}
          </p>
        </div>
      </div>
    );
  }

  if (!assessment) return null;

  if (submitted) {
    return (
      <div data-assessment-state="submitted" style={{
        minHeight: '100vh',
        backgroundColor: '#031128',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '500px',
          width: '90%'
        }}>
          <i className="fa-solid fa-check-circle" style={{ fontSize: '64px', color: '#28a745', marginBottom: '20px' }}></i>
          <h2 style={{ color: "var(--app-neutral-ink, #000)", marginBottom: '15px' }}>{t('assessmentViewer.submittedTitle')}</h2>
          <p style={{ color: '#666', fontSize: '16px', margin: 0 }}>
            {t('assessmentViewer.submittedBody')}
          </p>
        </div>
      </div>
    );
  }

  const questions = assessment?.questions || [];
  const totalQuestions = questions.length;
  const activeQuestionIndex = totalQuestions ? Math.min(Math.max(currentQuestionIndex, 0), totalQuestions - 1) : 0;
  const activeQuestion = questions[activeQuestionIndex] || null;
  const answeredCount = getAnsweredCount();
  const progressPercent = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;
  const activeQuestionAnswered = isQuestionAnswered(activeQuestion);
  const activeQuestionUploading = !!(activeQuestion && uploading[activeQuestion.id]);
  const isLastQuestion = totalQuestions > 0 && activeQuestionIndex === totalQuestions - 1;
  const submitBlocked = submitting || isSubmissionInProgress || submissionLocked || !activeQuestionAnswered || activeQuestionUploading || answeredCount < totalQuestions;
  const currentAnswerHint = activeQuestion?.type === 'record-video'
    ? t('assessmentViewer.recordToContinue')
    : activeQuestion?.type === 'file-upload'
      ? t('assessmentViewer.uploadToContinue')
      : t('assessmentViewer.completeToContinue');

  return (
    <div
      className="av-shell"
      style={{
        fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat',
        direction: isRTL ? 'rtl' : 'ltr'
      }}
    >
      <header className="av-topbar">
        <div className="av-title-wrap">
          <i className="fa-solid fa-clipboard-list"></i>
          <div>
            <h1>{assessment.title}</h1>
            <p>
              {assessmentStarted && totalQuestions
                ? t('assessmentViewer.headerQuestion', { number: activeQuestionIndex + 1, total: totalQuestions })
                : t('assessmentViewer.headerIntro')}
            </p>
          </div>
        </div>

        {assessmentStarted && timeRemaining !== null && (
          <div className={`av-timer ${timeRemaining < 300 ? 'danger' : ''}`}>
            <i className="fa-solid fa-clock"></i>
            {formatTime(timeRemaining)}
          </div>
        )}
      </header>

      <main className="av-main">
        <section className="av-progress-card">
          <div className="av-progress-head">
            <span>
              {assessmentStarted
                ? t('assessmentViewer.progress', { answered: answeredCount, total: totalQuestions })
                : t('assessmentViewer.readyProgress', { total: totalQuestions })}
            </span>
            <span>{t('assessmentViewer.progressPercent', { percent: progressPercent })}</span>
          </div>
          <div className="av-progress-track">
            <div style={{ width: `${progressPercent}%` }}></div>
          </div>
        </section>

        {!assessmentStarted ? (
          <section className="av-card av-intro-card">
            <div className="av-intro-head">
              <div className="av-intro-icon">
                <i className="fa-solid fa-circle-info"></i>
              </div>
              <div>
                <span className="av-eyebrow">{t('assessmentViewer.instructionsEyebrow')}</span>
                <h2>{t('assessmentViewer.instructionsTitle')}</h2>
                <p>
                  {assessment.instructions || t('assessmentViewer.instructionsBody', {
                    count: totalQuestions,
                    minutes: assessment.duration_minutes || 0
                  })}
                </p>
              </div>
            </div>

            <div className="av-intro-grid">
              <div className="av-intro-stat">
                <i className="fa-solid fa-list-check"></i>
                <span>{t('assessmentViewer.questionCountLabel')}</span>
                <strong>{totalQuestions}</strong>
              </div>
              <div className="av-intro-stat">
                <i className="fa-solid fa-clock"></i>
                <span>{t('assessmentViewer.timeLimitLabel')}</span>
                <strong>
                  {assessment.duration_minutes
                    ? t('assessmentViewer.minutesCount', { minutes: assessment.duration_minutes })
                    : t('assessmentViewer.untimed')}
                </strong>
              </div>
              <div className="av-intro-stat">
                <i className="fa-solid fa-shield-check"></i>
                <span>{t('assessmentViewer.progressSavedLabel')}</span>
                <strong>{t('assessmentViewer.autoSaveShort')}</strong>
              </div>
            </div>

            <div className="av-protection-note">
              <i className="fa-solid fa-lock"></i>
              <span>{t('assessmentViewer.introProtection')}</span>
            </div>

            {totalQuestions === 0 ? (
              <div className="av-warning">
                <i className="fa-solid fa-triangle-exclamation"></i>
                <span>{t('assessmentViewer.noQuestionsBody')}</span>
              </div>
            ) : (
              <button className="av-primary-btn av-begin-btn" onClick={handleBeginAssessment}>
                <span>{answeredCount > 0 ? t('assessmentViewer.continueAssessment') : t('assessmentViewer.begin')}</span>
                <i className="fa-solid fa-arrow-right"></i>
              </button>
            )}
          </section>
        ) : (
          <section className="av-card av-question-card" data-active-question>
            {activeQuestion ? (
              <>
                <div className="av-question-meta-row">
                  <span className="av-question-step">
                    {t('assessmentViewer.questionLabel', { number: activeQuestionIndex + 1, total: totalQuestions })}
                  </span>
                  <span className="av-question-type">
                    <i className={getQuestionTypeIcon(activeQuestion.type)}></i>
                    {getQuestionTypeLabel(activeQuestion.type)}
                  </span>
                </div>

                <h2 className="av-question-text">{activeQuestion.text}</h2>

                <div className="av-answer-panel">
                  {renderAnswerInput(activeQuestion)}
                </div>

                {!activeQuestionAnswered && (
                  <div className="av-inline-hint">
                    <i className="fa-solid fa-circle-info"></i>
                    <span>{currentAnswerHint}</span>
                  </div>
                )}

                {isLastQuestion && answeredCount < totalQuestions && activeQuestionAnswered && (
                  <div className="av-warning">
                    <i className="fa-solid fa-triangle-exclamation"></i>
                    <span>{t('assessmentViewer.incomplete', { remaining: totalQuestions - answeredCount })}</span>
                  </div>
                )}

                <div className="av-navigation">
                  <button
                    className="av-secondary-btn"
                    onClick={handlePreviousQuestion}
                    disabled={activeQuestionIndex === 0}
                  >
                    <i className="fa-solid fa-arrow-left"></i>
                    <span>{t('assessmentViewer.previous')}</span>
                  </button>

                  {isLastQuestion ? (
                    <button
                      className="av-primary-btn"
                      onClick={submitAssessment}
                      disabled={submitBlocked}
                    >
                      {submitting || isSubmissionInProgress ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin"></i>
                          <span>{submitting ? t('assessmentViewer.submitting') : t('assessmentViewer.processing')}</span>
                        </>
                      ) : submissionLocked ? (
                        <>
                          <i className="fa-solid fa-lock"></i>
                          <span>{t('assessmentViewer.submissionLocked')}</span>
                        </>
                      ) : (
                        <>
                          <span>{t('assessmentViewer.submit')}</span>
                          <i className="fa-solid fa-paper-plane"></i>
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      className="av-primary-btn"
                      onClick={handleNextQuestion}
                      disabled={!activeQuestionAnswered || activeQuestionUploading}
                    >
                      <span>{activeQuestionUploading ? t('assessmentViewer.uploading') : t('assessmentViewer.nextQuestion')}</span>
                      <i className="fa-solid fa-arrow-right"></i>
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="av-empty-state">
                <i className="fa-solid fa-clipboard-question"></i>
                <h2>{t('assessmentViewer.noQuestionsTitle')}</h2>
                <p>{t('assessmentViewer.noQuestionsBody')}</p>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

/**
 * Preserve the public assessment route while selecting its permanent owner.
 * Only authenticated-encrypted `aiv1.` capabilities enter the live viewer;
 * every historical slug continues through the unchanged legacy assessment UI.
 */
const AssessmentViewer = () => {
  const { slug } = useParams();

  if (String(slug || '').startsWith('aiv1.')) {
    return <LiveAiInterviewViewer invitationToken={slug} />;
  }

  return <LegacyAssessmentViewer />;
};

export default AssessmentViewer; 
